import { apiBaseUrl, getApi, postApi } from "../api/client";
import { getGuestId } from "../utils/guest";

function readPayload(payload) {
  if (payload?.data) {
    return payload.data;
  }

  return payload || {};
}

function normalizeCartItem(item, index) {
  const productId = item.productId ?? item.ProductId ?? item.ProductID ?? `item-${index}`;
  const uomId = Number(item.uomId ?? item.UOMID ?? 0);
  const price = Number(item.price ?? item.Price ?? 0);
  const qty = Number(item.qty ?? item.Qty ?? 0);

  return {
    productId,
    cartKey: `${productId}-${uomId || "default"}`,
    name: item.name || item.Name || "Product",
    price,
    qty,
    total: Number(item.total ?? item.Total ?? price * qty),
    imageUrl: item.imageUrl || item.ImageUrl || "",
    uomId,
    uomDesc: item.uomDesc || item.UOMDesc || "",
    unitPerCarton: Number(item.unitPerCarton ?? item.UnitPerCarton ?? item.UnitsPerCarton ?? 1),
    gst: Boolean(item.gst ?? item.GST ?? item.isGST ?? item.IsGST ?? false),
  };
}

function normalizeCartResponse(payload) {
  const data = payload || {};
  const result = data.Result || {};

  if (String(data.ResponseCode || "") !== "200") {
    throw new Error(data.Message || "Cart request failed.");
  }

  return {
    items: (result.Items || []).map(normalizeCartItem),
  };
}

function getSelectedUom(productOrId) {
  if (!productOrId || typeof productOrId !== "object") {
    return {
      uomId: 2,
      uomDesc: "UNIT",
      unitPerCarton: 1,
      price: 0,
    };
  }

  const dynamicUom = productOrId.dynamicUOM || [];
  const selectedUom = dynamicUom.length > 1 ? productOrId.selectedUOM : null;

  return {
    uomId: selectedUom?.uomId || 2,
    uomDesc: selectedUom?.uomDesc || "UNIT",
    unitPerCarton: selectedUom?.unitPerCarton || productOrId.unitPerCarton || 1,
    price: selectedUom?.price || productOrId.price || 0,
  };
}

export async function addToCart(productOrId, qty = 1) {
  const productId = typeof productOrId === "object" ? productOrId.productId : productOrId;
  const selectedUom = getSelectedUom(productOrId);

  const response = await postApi(
    process.env.REACT_APP_ADD_TO_CART_ENDPOINT || "/api/Products/AddToCart",
    {
      GuestId: getGuestId(),
      ProductID: productId,
      ProductId: productId,
      Qty: qty,
      UOMID: Number(selectedUom.uomId || 0),
      UOMDesc: selectedUom.uomDesc || "",
      UnitPerCarton: Number(selectedUom.unitPerCarton || 1),
      Price: Number(selectedUom.price || 0),
    }
  );

  const payload = readPayload(response.data);

  if (String(payload.ResponseCode || "") !== "200") {
    throw new Error(payload.Message || "Add to cart failed.");
  }

  return getCart();
}

export async function getCart() {
  const response = await getApi(
    process.env.REACT_APP_GET_CART_ENDPOINT || "/api/Products/GetCart",
    {
      params: {
        guestId: getGuestId(),
      },
    }
  );

  return normalizeCartResponse(readPayload(response.data));
}

function getCartItemIdentifiers(itemOrProductId) {
  if (itemOrProductId && typeof itemOrProductId === "object") {
    return {
      productId: itemOrProductId.productId ?? itemOrProductId.ProductId ?? itemOrProductId.ProductID,
      uomId: itemOrProductId.uomId ?? itemOrProductId.UOMID ?? 0,
    };
  }

  return {
    productId: itemOrProductId,
    uomId: 0,
  };
}

export async function removeItem(itemOrProductId) {
  const { productId } = getCartItemIdentifiers(itemOrProductId);
  const response = await postApi(
    process.env.REACT_APP_REMOVE_ITEM_ENDPOINT || "/api/Products/RemoveItem",
    {
      guestId: getGuestId(),
      productId,
    }
  );

  const payload = readPayload(response.data);

  if (payload.ResponseCode && String(payload.ResponseCode) !== "200") {
    throw new Error(payload.Message || "Remove item failed.");
  }

  return getCart();
}

export async function updateCartItem(itemOrProductId, quantity) {
  const { productId } = getCartItemIdentifiers(itemOrProductId);
  const response = await postApi(
    process.env.REACT_APP_UPDATE_CART_QTY_ENDPOINT || "/api/Products/UpdateCartQty",
    {
      guestId: getGuestId(),
      productId,
      qty: quantity,
    }
  );

  const payload = readPayload(response.data);

  if (String(payload.ResponseCode || "") !== "200") {
    throw new Error(payload.Message || "Update cart qty failed.");
  }

  return getCart();
}

export async function checkout(data) {
  const pickupDateIso = data.pickupDate
    ? new Date(`${data.pickupDate}T12:00:00`).toISOString()
    : null;

  const orderEndpoint = process.env.REACT_APP_PLATTER_ORDER_ENDPOINT || "/api/Products/PlaceGuestPlatterOrder";
  const checkoutPayload = {
    guestId: getGuestId(),
    name: data.name,
    phone: data.phone,
    email: data.email,
    warehouse: data.pickup?.warehouseCode || data.pickup?.warehouse,
    pickupLocationId: data.pickup?.pickupId,
    pickupDate: pickupDateIso,
    paymentIntentId: data.paymentIntentId || data.paymentId,
    paymentStatus: data.paymentStatus || "succeeded",
    amount: data.grandTotal || 0,
    gst: data.gstTotal || 0,
    grandTotal: data.grandTotal || 0,
    items: (data.cartItems || []).map((item) => ({
      productId: item.productId,
      productName: item.name,
      quantity: item.qty || 0,
      price: item.price || 0,
      total: item.total || 0,
      uom: item.uomDesc || "UNIT",
    })),
  };

  console.log("order/place order API URL", `${apiBaseUrl}${orderEndpoint}`);
  console.log("full order payload", checkoutPayload);

  const response = await postApi(orderEndpoint, checkoutPayload);

  const payload = readPayload(response.data);
  console.log("place order API response", payload);

  if (String(payload.ResponseCode || "") !== "200") {
    throw new Error(payload.Message || "Checkout failed.");
  }

  return payload;
}
